// ─────────────────────────────────────────────────────────────
// MarkdownChatBody — render an assistant chat message as markdown
// inside a chat bubble, styled to match MUI body2 typography.
//
// Why this exists: Marie and Jamie both stream markdown source
// (e.g. `**Pixel size:** 26.04 nm/pixel`, `- bullet`). Rendering
// that as plain text inside a <Typography> shows the asterisks
// literally instead of bolding the run. This component delegates
// to react-markdown (already in the dep tree at v9.1.0) and
// overrides the default element renderers so the result still
// looks like clean MUI body2 typography rather than a stylized
// chat-bot blob.
//
// Streaming cursor: if `isStreaming` is true, an inline blinking
// cursor span is appended at the visual end of the LAST paragraph
// produced by the markdown renderer. We tag the source content
// with a zero-width sentinel before parsing; the custom `p`
// component locates that sentinel in its rendered children and
// replaces it with the cursor element. This survives whatever
// markdown happens to be at the tail (bold, italic, list item,
// heading, etc.) because react-markdown still emits the sentinel
// as a plain text node in the last leaf.
//
// IMPORTANT: this component is shared by Marie (`AiPanel.tsx`)
// and Jamie (`JamieChatView.tsx`). Any styling change here
// affects both — keep that intentional.
// ─────────────────────────────────────────────────────────────

import { Children, cloneElement, isValidElement, type ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import ReactMarkdown, { type Components } from "react-markdown";

// Sentinel — appended to streaming content so the custom `p` /
// `li` renderer can find "the very end of the last block" in
// the parsed React tree without having to walk the AST ahead of
// time. The sequence is intentionally unguessable so it can't
// collide with anything Marie or Jamie would actually emit.
// Zero-width joiners (‍) flank a tag of underscore-bounded
// uppercase letters. Markdown parsers treat zero-width chars
// as ordinary text, so the sentinel survives parsing intact
// and lands as a plain string node at the tail of the tree.
// Note: avoid `_` (italic) and `*` (bold/italic) and `` ` `` (code)
// in the sentinel body — markdown would try to parse those.
// Plain zero-width joiners and ASCII letters are safe.
const STREAMING_SENTINEL = "‍MSHSTREAMCURSORZ‍";

interface MarkdownChatBodyProps {
  /**
   * The markdown source to render. Code fences MUST be stripped
   * by the caller before being handed in — Marie sends code to
   * the editor, Jamie doesn't emit code, so a fenced block in
   * the bubble is always a bug we want to surface defensively.
   */
  content: string;
  /**
   * When true, append a blinking cursor at the end of the last
   * paragraph. Caller is responsible for deciding when streaming
   * is "active" (e.g. suppress while inside an open code fence).
   */
  isStreaming?: boolean;
  /**
   * MUI palette path or CSS color for the streaming cursor.
   * Marie uses "primary.main"; Jamie uses "currentColor" to
   * blend with her colored bubble's contrast text.
   */
  cursorColor?: string;
}

/**
 * Build the `components` map for ReactMarkdown. Returned as a
 * function rather than a constant so we can close over the
 * streaming flag and inject the cursor in the right paragraph.
 */
function buildComponents(opts: {
  isStreaming: boolean;
  cursorColor: string;
}): Components {
  const { isStreaming, cursorColor } = opts;

  // Module-call-scoped guard: the sentinel appears exactly once
  // in the source, but it could land in any of several block
  // containers depending on what Marie was mid-emitting (a
  // paragraph, a list item, a heading, a blockquote, …). Each
  // block renderer calls `injectCursor` defensively; this flag
  // guarantees we still produce only one cursor element per
  // render even if the recursion paths overlap.
  const renderState = { injected: false };

  // Shared heading renderer — `level` controls the semantic tag
  // but visually all six look the same (body2 size, bold). This
  // mirrors the chat convention that headings in a small bubble
  // shouldn't take up extra real estate.
  const renderHeading = (
    level: "h1" | "h2" | "h3" | "h4" | "h5" | "h6",
    children: ReactNode
  ) => (
    <Typography
      variant="body2"
      component={level}
      sx={{ fontWeight: 700, mb: "0.4em", "&:first-of-type": { mt: 0 }, lineHeight: "inherit" }}
    >
      {children}
    </Typography>
  );

  // The cursor element. Identical visual contract to the prior
  // inline-Typography cursor in AiPanel: 2px wide, 0.85em tall,
  // primary background, blinking on a 1s step.
  const Cursor = () => (
    <Box
      component="span"
      aria-hidden
      sx={{
        display: "inline-block",
        width: "2px",
        height: "0.85em",
        bgcolor: cursorColor,
        verticalAlign: "text-bottom",
        ml: "1px",
        "@keyframes marieBlink": {
          "0%, 100%": { opacity: 1 },
          "50%": { opacity: 0 },
        },
        animation: "marieBlink 1s step-start infinite",
      }}
    />
  );

  /**
   * Walk a children array and replace any text node whose value
   * contains the sentinel with [textBefore, <Cursor />]. Returns
   * a new children array. Non-string children pass through
   * unchanged. We only need to handle the first match — the
   * sentinel is appended exactly once per content snapshot.
   */
  function injectCursor(children: ReactNode): ReactNode[] {
    const out: ReactNode[] = [];
    Children.forEach(children, (child) => {
      if (renderState.injected) {
        // Once we've placed the cursor for this render, we still
        // need to scrub any stray sentinel text that survived in
        // a sibling subtree (shouldn't happen for well-formed
        // input, but handle the impossible just in case).
        if (typeof child === "string") {
          out.push(child.split(STREAMING_SENTINEL).join(""));
        } else {
          out.push(child);
        }
        return;
      }
      if (typeof child === "string") {
        const idx = child.indexOf(STREAMING_SENTINEL);
        if (idx !== -1) {
          const before = child.slice(0, idx);
          const after = child.slice(idx + STREAMING_SENTINEL.length);
          if (before) out.push(before);
          out.push(<Cursor key="__streaming_cursor__" />);
          if (after) out.push(after);
          renderState.injected = true;
          return;
        }
        out.push(child);
        return;
      }
      // For element children we recurse into their children,
      // then clone them with the new children. This handles the
      // case where the sentinel ends up nested inside a
      // <strong>, <em>, <code>, etc. at the tail of the last
      // paragraph (e.g. content like "**Pixel size:** 26.04").
      if (isValidElement(child)) {
        const props = child.props as { children?: ReactNode };
        const innerNodes = injectCursor(props.children);
        if (renderState.injected) {
          out.push(cloneElement(child, undefined, ...innerNodes));
          return;
        }
      }
      out.push(child);
    });
    return out;
  }

  return {
    // Paragraph — the workhorse. Tight margins so successive
    // paragraphs read like body2 prose, not like blog post
    // double-spaced paragraphs.
    p: ({ children }) => {
      const finalChildren = isStreaming ? injectCursor(children) : children;
      return (
        <Typography
          variant="body2"
          component="p"
          sx={{
            m: 0,
            "&:not(:last-child)": { mb: "0.5em" },
            lineHeight: "inherit",
            wordBreak: "break-word",
          }}
        >
          {finalChildren}
        </Typography>
      );
    },

    // Bold / italic — let MUI handle the rendering, just
    // tighten font weight to MUI's standard 600 (matches the
    // existing `& strong` rule in JamieChatView).
    strong: ({ children }) => (
      <Box component="strong" sx={{ fontWeight: 600 }}>
        {children}
      </Box>
    ),
    em: ({ children }) => (
      <Box component="em" sx={{ fontStyle: "italic" }}>
        {children}
      </Box>
    ),

    // Lists — small vertical margin, modest left padding so the
    // bullet sits just inside the bubble edge. body2 size on the
    // <li> via Typography variant inheritance.
    ul: ({ children }) => (
      <Box
        component="ul"
        sx={{
          mt: "0.25em",
          mb: "0.25em",
          pl: "1.25em",
          "&:first-of-type": { mt: 0 },
          "&:last-child": { mb: 0 },
        }}
      >
        {children}
      </Box>
    ),
    ol: ({ children }) => (
      <Box
        component="ol"
        sx={{
          mt: "0.25em",
          mb: "0.25em",
          pl: "1.25em",
          "&:first-of-type": { mt: 0 },
          "&:last-child": { mb: 0 },
        }}
      >
        {children}
      </Box>
    ),
    li: ({ children }) => {
      const finalChildren = isStreaming ? injectCursor(children) : children;
      return (
        <Typography
          variant="body2"
          component="li"
          sx={{ m: 0, lineHeight: "inherit" }}
        >
          {finalChildren}
        </Typography>
      );
    },

    // Headings — Marie shouldn't be emitting H1 in a chat reply,
    // but if she does we knock it down to body2 + bold so it
    // doesn't blow out the bubble. All six levels share the same
    // treatment for consistency. Each renderer also injects the
    // streaming cursor in case Marie's tail token landed in a
    // heading line.
    h1: ({ children }) => renderHeading("h1", isStreaming ? injectCursor(children) : children),
    h2: ({ children }) => renderHeading("h2", isStreaming ? injectCursor(children) : children),
    h3: ({ children }) => renderHeading("h3", isStreaming ? injectCursor(children) : children),
    h4: ({ children }) => renderHeading("h4", isStreaming ? injectCursor(children) : children),
    h5: ({ children }) => renderHeading("h5", isStreaming ? injectCursor(children) : children),
    h6: ({ children }) => renderHeading("h6", isStreaming ? injectCursor(children) : children),

    // Inline code — subtle hover-bg pill, no border. This is
    // what Marie reaches for when she names an API call inline
    // like `bridge.get_current_image()`. react-markdown v9
    // collapses inline and fenced code into the same `code`
    // renderer; the outer `pre` renderer wrapping fenced code
    // resets the background, padding, and border-radius so
    // fenced blocks read as a block, not as a bubbly pill.
    code: ({ children }) => (
      <Box
        component="code"
        sx={{
          fontFamily: '"Roboto Mono", ui-monospace, monospace',
          fontSize: "0.85em",
          bgcolor: "action.hover",
          px: "0.4em",
          py: "0.2em",
          borderRadius: "4px",
        }}
      >
        {children}
      </Box>
    ),

    // Fenced code block — should not happen in practice (Marie's
    // code goes to the editor; Jamie doesn't emit code). Render
    // defensively as a monospace block so a stray fence isn't
    // catastrophic, but the goal is "this should rarely fire."
    pre: ({ children }) => (
      <Box
        component="pre"
        sx={{
          m: 0,
          mt: "0.5em",
          mb: "0.5em",
          p: 1,
          bgcolor: "action.hover",
          borderRadius: "4px",
          fontFamily: '"Roboto Mono", ui-monospace, monospace',
          fontSize: "0.85em",
          whiteSpace: "pre",
          overflowX: "auto",
          "& code": {
            bgcolor: "transparent",
            p: 0,
            borderRadius: 0,
          },
        }}
      >
        {children}
      </Box>
    ),

    // Links — Marie may emit Maps Bridge doc URLs. Open in a new
    // tab and use the safe relationship attributes.
    a: ({ children, href }) => (
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: "primary.main",
          textDecoration: "underline",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {children}
      </Box>
    ),

    // Horizontal rule — thin divider, small vertical margin.
    hr: () => (
      <Box
        component="hr"
        sx={{
          border: 0,
          borderTop: "1px solid",
          borderColor: "divider",
          my: "0.5em",
        }}
      />
    ),

    // Blockquote — left rule + secondary text color. Marie's
    // doc-quoting style uses these occasionally.
    blockquote: ({ children }) => {
      const finalChildren = isStreaming ? injectCursor(children) : children;
      return (
        <Box
          component="blockquote"
          sx={{
            m: 0,
            mt: "0.25em",
            mb: "0.25em",
            pl: 1,
            borderLeft: "2px solid",
            borderColor: "divider",
            color: "text.secondary",
          }}
        >
          {finalChildren}
        </Box>
      );
    },
  };
}

export function MarkdownChatBody({
  content,
  isStreaming = false,
  cursorColor = "primary.main",
}: MarkdownChatBodyProps) {
  // When streaming, append the sentinel so the custom `p`
  // renderer knows where to inject the cursor. We don't append
  // it for non-streaming snapshots — there's nothing to inject
  // and skipping the work keeps memo'd renders cheaper.
  const source = isStreaming ? `${content}${STREAMING_SENTINEL}` : content;

  // Wrapper Box gives the markdown a deterministic font size /
  // line height to inherit from. Each block-level renderer above
  // is body2 anyway, but the wrapper covers the (very rare) case
  // where react-markdown emits a node we haven't overridden.
  return (
    <Box
      sx={{
        typography: "body2",
        wordBreak: "break-word",
      }}
    >
      <ReactMarkdown components={buildComponents({ isStreaming, cursorColor })}>
        {source}
      </ReactMarkdown>
    </Box>
  );
}
